import {
  afterNextRender, ChangeDetectionStrategy, Component, computed, effect, ElementRef, HostBinding, inject,
  input, model, OnDestroy, output, PLATFORM_ID, Renderer2, signal, viewChild
} from '@angular/core';

import { isPlatformBrowser } from '@angular/common';

import { ImageDsService } from '../../../_services/image-ds.service';
import { ImageRatioEnum } from '../../../_enums/image-ratio.enum';
import { IMedia } from '../../../_interfaces/media.interface';
import { MediaTypeEnum } from '../banner/banner.enum';

const SEBPLATFORM_IMAGE_URL = 'https://sebplatform.api.groupe-seb.com';

@Component({
  selector: 'sd-media[image]',
  templateUrl: './media-image.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
})
export class SdMediaImageComponent implements OnDestroy {
  media = input<IMedia>();
  width = input<number>();
  height = input<number>();
  customId = input<string>();
  mediaTitle = input<string>();
  imgRatio = input<ImageRatioEnum>();
  hasAltText = input(true);
  isAriaHidden = input(false);
  play = model(false);
  twicPicsAutoSize = input(true);
  isFit = input<boolean>();
  isGif = input<boolean>();
  isZoomed = input(false);
  zoomLevel = input(0);
  class = input('');

  imageIsLoaded = output<void>();

  @HostBinding('class')
  get hostClasses(): string {
    return this.computedHostClasses();
  }

  /**
   * Overriding twicpics image quality because default 70% is sometimes blurry.
   * @link https://www.twicpics.com/docs/api/transformations#quality
   * TODO Changer la qualité une fois qu'une décision aura été prise par rapport aux perfs
   */
  private readonly twicpicsQuality = 70;

  readonly targetMedia = viewChild<ElementRef<HTMLImageElement>>('element');

  private readonly imageDsService = inject(ImageDsService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly renderer = inject(Renderer2);
  public readonly elRef = inject(ElementRef);

  mediaTypeClass = signal<string>(undefined);
  mediaType = signal<MediaTypeEnum>(undefined);
  twicError = signal(false);
  fallbackUrl = signal('');
  gifUrl = signal('');

  changes: MutationObserver;

  computedHostClasses = computed(() => {
    const zoomLevel = this.zoomLevel();
    const isZoomed = this.isZoomed();
    const ratio = this.imgRatio();

    return [
      this.class(),
      this.isFit() ? 'is-fit' : '',
      ratio ? 'is-' + ratio : '',
      this.media()?.urlTwicPic && !this.twicError() && this.twicPicsAutoSize() ? 'is-twicpics' : '',
      this.mediaTypeClass(),
      isZoomed ? 'zoomed' : '',
      zoomLevel > 0 ? `zoom-level-${zoomLevel}` : ''
    ]
      .filter(Boolean)
      .join(' ');
  });

  twicpicsTransform = computed(() => {
    if (this.media()?.urlTwicPic) {
      return `quality=${this.twicpicsQuality}/${this.isFit() ? 'contain' : 'cover-max'}`;
    }
    return '';
  });

  twicpicsUrl = computed(() => {
    const media = this.media();
    if (media?.urlTwicPic) {
      const previewUrl = media.urlTwicPic.replace('image:/', '');
      const prefix = previewUrl.includes('?') ? '&' : '?';
      return `https://${media.twicPicsDomain}/${previewUrl}${prefix}twic=v1/output=preview`;
    }
    return '';
  });

  manualTwicpics = computed(() => {
    const media = this.media();
    if (media?.urlTwicPic && !this.twicPicsAutoSize()) {
      let mediaUrl = media.urlTwicPic;
      if (mediaUrl.startsWith('image:/')) {
        mediaUrl = mediaUrl.replace('image:/', '');
      }
      return {
        url: `https://${media.twicPicsDomain}/${mediaUrl}`,
        transform: `${mediaUrl.includes('?') ? '&' : '?'}twic=v1/quality=${this.twicpicsQuality}/resize=`
      };
    }
    return undefined;
  });

  imgWidth = computed(() => {
    const width = this.width();
    return width ? width + 'px' : undefined;
  });

  imgHeight = computed(() => {
    const height = this.height();
    return height ? height + 'px' : undefined;
  });

  ariaHidden = computed(() => this.isAriaHidden() ? true : undefined);
  ariaRole = computed(() => this.isAriaHidden() ? 'presentation' : 'img');
  dataType = computed(() => this.media()?.mime?.split('/')[1]);
  altText = computed(() => this.hasAltText() ? this.media()?.altText ?? '' : '');

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      afterNextRender(() => {
        this.setMediaType();
        this.imageDsService.sendImageDatas(this.media());
      });

      effect(() => {
        const target = this.targetMedia();

        if (target?.nativeElement) {
          this.changes?.disconnect();

          this.changes = new MutationObserver(() => {
            this.observeTwicpics();
          });

          this.changes.observe(target.nativeElement, {
            attributeFilter: ['class']
          });
        }
      });

      effect(() => {
        if (this.twicPicsAutoSize()) {
          this.setPreview(this.twicpicsUrl());
          this.setPreviewSize(this.isFit());
        }
      });

      effect(() => {
        const media = this.media();
        this.fallbackUrl.set(media?.urlTwicPic || media?.url || '');
      });

      effect(() => {
        const target = this.targetMedia();
        if (target && this.isGif()) {
          target.nativeElement.setAttribute('crossorigin', 'anonymous');
        }
      });

      effect(() => {
        if (this.gifUrl()) {
          if (this.play()) {
            this.playMedia();
          } else {
            this.pauseMedia();
          }
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.changes?.disconnect();
  }

  playMedia(): void {
    const el = this.targetMedia()?.nativeElement;
    if (el) {
      const canvas = el.parentNode.querySelector('canvas');
      if (canvas) {
        this.renderer.removeChild(el.parentNode, canvas);
      }

    }
  }

  pauseMedia(): void {
    const el = this.targetMedia()?.nativeElement;
    if (el) {
      const cWidth = el.width;
      const cHeight = el.height;
      const canvas = this.renderer.createElement('canvas');

      canvas.width = cWidth;
      canvas.height = cHeight;

      canvas.getContext('2d').drawImage(el, 0, 0, cWidth, cHeight);

      canvas.style.position = 'absolute';
      canvas.style.zIndex = '1';
      canvas.setAttribute('aria-hidden', 'true');
      canvas.setAttribute('role', 'presentation');

      this.renderer.insertBefore(el.parentNode, canvas, el);
    }
  }

  removePreview(): void {
    this.renderer.removeStyle(this.elRef.nativeElement, 'backgroundImage');
    this.imageLoaded();
  }

  imageLoaded(): void {
    this.imageIsLoaded.emit();
  }

  fallbackToMedia(): void {
    const url = this.media()?.url;

    if (url && url.startsWith(SEBPLATFORM_IMAGE_URL) && !url.includes('w=') && !url.includes('fit=')) {
      this.fallbackUrl.set(url?.replace('original/', '') + '?w=1920&fit=scale');
    }

    this.targetMedia()?.nativeElement.classList.remove('twic-error');
    this.twicError.set(true);
    this.removePreview();
  }

  private setMediaType(): void {
    if (this.isGif()) {
      this.gifUrl.set(this.media()?.url);
      this.mediaType.set(MediaTypeEnum.gif);
      this.mediaTypeClass.set(`is-media-${MediaTypeEnum.image} is-media-${MediaTypeEnum.gif}`);
    } else {
      this.mediaType.set(MediaTypeEnum.image);
      this.mediaTypeClass.set(`is-media-${MediaTypeEnum.image}`);
    }
  }

  private observeTwicpics(): void {
    const target = this.targetMedia();

    if (target?.nativeElement) {
      const classList = target.nativeElement.classList;
      const error = classList.contains('twic-error');
      const done = classList.contains('twic-done');

      if (done || error) {
        this.removePreview();

        if (error) {
          this.fallbackToMedia();
        }
      }
    }
  }

  private setPreview(url: string): void {
    if (url) {
      this.renderer.setStyle(
        this.elRef.nativeElement,
        'backgroundImage',
        `url(${url})`
      );
    }
  }

  private setPreviewSize(isFit: boolean): void {
    if (this.twicPicsAutoSize()) {
      this.renderer.setStyle(
        this.elRef.nativeElement,
        'backgroundSize',
        isFit ? 'contain' : 'cover',
      );
    }
  }
}
